import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, X, Eye, EyeOff, Icon } from '../ui/Icon';
import { authApi } from '../../api/auth.api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../ui/Toast';
import Spinner from '../ui/Spinner';

const MIN_PASSWORD_LENGTH = 8;

interface Props {
  onClose: () => void;
}

export default function ChangePasswordModal({ onClose }: Props) {
  const { t } = useTranslation();
  const { setAuth } = useAuthStore();
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showNext, setShowNext] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const tooShort  = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch  = confirm.length > 0 && next !== confirm;
  const canSubmit = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === confirm;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // The server revokes every other session and hands back a fresh pair for
      // this one, so the token in the store has to be swapped or the next call
      // goes out with a token whose refresh is already dead.
      const { accessToken, user } = await authApi.changePassword({ currentPassword: current, newPassword: next });
      setAuth(user, accessToken);
      toast(t('account.changePassword.success'), 'success');
      onClose();
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (code === 'CURRENT_PASSWORD_INVALID')   setError(t('account.changePassword.wrongCurrent'));
      else if (code === 'PASSWORD_UNCHANGED')    setError(t('account.changePassword.unchanged'));
      else if (code === 'NO_PASSWORD_SET')       setError(t('account.changePassword.googleAccount'));
      else                                       setError(t('account.changePassword.failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <Icon as={KeyRound} size={20} className="text-teal-600 dark:text-teal-400" />
            <h2 className="font-semibold text-slate-900 dark:text-slate-100">{t('account.changePassword.title')}</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
            <Icon as={X} size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label htmlFor="current-pw" className="label">{t('account.changePassword.current')}</label>
            <input
              id="current-pw" type="password"
              className="input"
              value={current} onChange={e => setCurrent(e.target.value)}
              required autoFocus autoComplete="current-password"
            />
          </div>

          <div>
            <label htmlFor="next-pw" className="label">{t('account.changePassword.new')}</label>
            <div className="relative">
              <input
                id="next-pw" type={showNext ? 'text' : 'password'}
                className="input pr-10"
                placeholder={t('auth.login.passwordPlaceholder')}
                value={next} onChange={e => setNext(e.target.value)}
                required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNext(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                aria-label={showNext ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                tabIndex={-1}
              >
                <Icon as={showNext ? EyeOff : Eye} size={16} />
              </button>
            </div>
            {tooShort && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">
                {t('auth.resetPassword.tooShort', { min: MIN_PASSWORD_LENGTH })}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="confirm-pw" className="label">{t('account.changePassword.confirm')}</label>
            <input
              id="confirm-pw" type={showNext ? 'text' : 'password'}
              className="input"
              value={confirm} onChange={e => setConfirm(e.target.value)}
              required autoComplete="new-password"
            />
            {mismatch && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">
                {t('auth.resetPassword.mismatch')}
              </p>
            )}
          </div>

          <p className="text-xs text-slate-400 dark:text-slate-500">
            {t('account.changePassword.signsOutOthers')}
          </p>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={loading || !canSubmit} className="btn-primary flex-1">
              {loading ? <Spinner size="sm" /> : t('account.changePassword.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
